import { SelectOption, TableHeader } from '@common/types/common';
import { FOOD_PREFERENCE, IUser, Roles, UserList } from '@common/types/user';
import { ROLE_TO_NAME_MAP } from '@constants/account';
import { capitalizeFirstLetter } from '@utils/common';
import { IntlShape } from 'react-intl';

const getRoleName = (role: string, intl?: IntlShape) => {
    if (intl) {
        return ROLE_TO_NAME_MAP(intl).get(role as Roles) ?? role;
    }

    // For dashboard routes translating would mean incosistency so we maintain the old
    // logic of capitalizing the role name and replacing underscores
    let roleName = role === 'victron' ? 'Administrator' : role;
    if (roleName.includes('_')) {
        roleName = roleName
            .split('_')
            .map((x) => capitalizeFirstLetter(x))
            .join(' ');
    } else {
        roleName = capitalizeFirstLetter(roleName);
    }
    return roleName;
};

const FOOD_PREFERENCES = (intl: IntlShape): SelectOption<NonNullable<IUser['food_preference']>>[] => [
    {
        label: intl.formatMessage({ id: 'foodPreference.noPreference', defaultMessage: 'No preference' }),
        value: FOOD_PREFERENCE.NO_PREFERENCE,
    },
    {
        label: intl.formatMessage({ id: 'foodPreference.vegetarian', defaultMessage: 'Vegetarian' }),
        value: FOOD_PREFERENCE.VEGETARIAN,
    },
    {
        label: intl.formatMessage({ id: 'foodPreference.pescetarian', defaultMessage: 'Pescetarian' }),
        value: FOOD_PREFERENCE.PESCETARIAN,
    },
    {
        label: intl.formatMessage({ id: 'foodPreference.nutFree', defaultMessage: 'Nut free' }),
        value: FOOD_PREFERENCE.NUT_FREE,
    },
    {
        label: intl.formatMessage({ id: 'foodPreference.glutenLactoseFree', defaultMessage: 'Gluten/lactose free' }),
        value: FOOD_PREFERENCE.GLUTEN_LACTOSE_FREE,
    },
    {
        label: intl.formatMessage({ id: 'foodPreference.vegan', defaultMessage: 'Vegan' }),
        value: FOOD_PREFERENCE.VEGAN,
    },
];

const FOOD_PREFERENCE_OPTIONS: SelectOption<NonNullable<IUser['food_preference']>>[] = [
    { label: 'No preference', value: FOOD_PREFERENCE.NO_PREFERENCE },
    { label: 'Vegetarian', value: FOOD_PREFERENCE.VEGETARIAN },
    { label: 'Pescetarian', value: FOOD_PREFERENCE.PESCETARIAN },
    { label: 'Nut free', value: FOOD_PREFERENCE.NUT_FREE },
    { label: 'Gluten/lactose free', value: FOOD_PREFERENCE.GLUTEN_LACTOSE_FREE },
    { label: 'Vegan', value: FOOD_PREFERENCE.VEGAN },
];

/**
 * Generates a secure password that meets the application's password requirements:
 * - At least 10 characters long
 * - Contains at least one lowercase letter
 * - Contains at least one uppercase letter
 * - Contains at least one number
 * - Contains at least one special character
 */
const generateSecurePassword = (length: number = 12): string => {
    if (length < 10) {
        throw new Error('Password length must be at least 10 characters');
    }

    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const specialChars = '!@#$%^&*()_-+={}[]|\\:;"\'<>,.?/~`';

    // Ensure at least one character from each required category
    const requiredChars = [
        lowercase[Math.floor(Math.random() * lowercase.length)],
        uppercase[Math.floor(Math.random() * uppercase.length)],
        numbers[Math.floor(Math.random() * numbers.length)],
        specialChars[Math.floor(Math.random() * specialChars.length)],
    ];

    // Fill the rest of the password length with random characters from all categories
    const allChars = lowercase + uppercase + numbers + specialChars;
    const remainingChars = Array.from(
        { length: length - 4 },
        () => allChars[Math.floor(Math.random() * allChars.length)]
    );

    // Combine required and remaining characters
    const passwordArray = [...requiredChars, ...remainingChars];

    // Shuffle the array to randomize character positions
    for (let i = passwordArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
    }

    return passwordArray.join('');
};

const STANDARD_USER_TABLE_HEADERS: TableHeader<UserList & { concatenated_name: string }>[] = [
    { id: 'concatenated_name', label: 'NAME', sortable: true },
    { id: 'company_name', label: 'COMPANY', sortable: true },
    { id: 'role', label: 'ROLE', sortable: false },
];

const isVictronAdmin = (user: Pick<IUser, 'role'>) =>
    [Roles.VICTRON, Roles.SALES_MANAGER, Roles.SALES_DIRECTOR].includes(user.role);

export {
    FOOD_PREFERENCE_OPTIONS,
    FOOD_PREFERENCES,
    generateSecurePassword,
    getRoleName,
    isVictronAdmin,
    STANDARD_USER_TABLE_HEADERS,
};
